import React from 'react'
// libs
import styled from 'styled-components'
import insertTextAtCursor from 'insert-text-at-cursor'
import { useSelector } from 'react-redux'
import { useApolloClient } from 'hooks/useApolloClient'
import { FieldArray, formValueSelector } from 'redux-form'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// components
import { ReduxInput } from 'components/FormElements'
import EmailEditor from './components/EmailEditor'
import renderRecipients from './components/Recipients'
import AddMetatagButton from 'components/Modals/SystemProcessModal/Form/components/Transactions/components/Transaction/components/SendEmail/components/EmailEditor/components/AddMetatagButton'
// graphql
import { RISK_TEMPLATE_QUERY } from 'graphql/risk/queries'
// views
import { FieldWrapper } from './views'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// types
import { Forms, Modals } from 'constants/index'
import { FieldSource, FieldType, IField, IRiskTemplate } from 'types/graphql'
// utils
import get from 'lodash/get'
import { required } from 'utils/validators'
import { handleError } from 'utils/errors/handlers'
import { getTemplateFieldsOptions } from 'utils/systemProcess'

const Wrapper = styled.div`
  .Wysiwyg-wrapperClassName {
    &:hover {
      border-width: 1px !important;
    }
  }

  .Wysiwyg-editorClassName {
    overflow: auto;

    > div {
      padding: 0;
      outline: none;
      height: 100%;
    }
  }
`

interface Props {
  field: string
}

const FIELD_STYLES = { marginTop: '16px' }

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

const filterBySource = ({ fieldType, source, active }) =>
  active &&
  fieldType === FieldType.Lookup &&
  source.some((src: FieldSource) => [FieldSource.Users, FieldSource.Positions].includes(src))

const SendEmail: React.FC<Props> = ({ field }) => {
  const { client } = useApolloClient()

  const { isRiskTemplate, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const [fieldsOptions, setFieldsOptions] = React.useState([])
  const [parentRiskTemplates, setParentRiskTemplates] = React.useState([])
  const [parentFieldsOptions, setParentFieldsOptions] = React.useState([])

  const inputRef = React.useRef<any>()

  const { subject, template, recipients } = useSelector((state) => ({
    subject: selector(state, `${field}.subject`),
    template: selector(state, `${field}.template`),
    recipients: selector(state, `${field}.recipients`)
  }))

  const { showModal } = useShowModal({ modalType: Modals.EMAIL_PREVIEW_MODAL })

  const showEmailPreview = (template: string) => showModal({ subject, template })

  const addMetatag = async (metatag: string, closePopup: () => void) => {
    const input = document.getElementById(`${field}.subject-input`)

    insertTextAtCursor(input, ` ${metatag} `)

    await closePopup()

    inputRef.current.focus()
  }

  const getFieldsOptions = async () => {
    try {
      const fields: IField[] = get(riskTemplate, 'fields', []).filter(filterBySource)

      const templatesIds = fields.filter(({ entityId }) => entityId !== riskTemplate.id).map(({ entityId }) => entityId)

      let templates: IRiskTemplate[] = []

      if (templatesIds.length) {
        templates = get(
          await client.query({
            fetchPolicy: 'no-cache',
            query: RISK_TEMPLATE_QUERY,
            variables: { filter: { ids: templatesIds } }
          }),
          'data.riskTemplates.data',
          []
        )
      }

      const options = fields.flatMap((field) => {
        const parentLabel = field.parentFieldId
          ? riskTemplate?.fields?.find((f) => f.id === field.parentFieldId)?.label
          : ''

        return getTemplateFieldsOptions({ field, templates, parentLabel })
      })

      setFieldsOptions(options)
    } catch (e) {
      handleError(e, 'SendEmail.getFieldsOptions')
    }
  }

  const getParentFieldsOptions = async () => {
    try {
      const templatesIds = riskTemplate?.relatedRiskTemplatesIdsFromSystemProcesses

      let parentTemplates: IRiskTemplate[] = []

      if (templatesIds?.length) {
        parentTemplates = get(
          await client.query({
            fetchPolicy: 'no-cache',
            query: RISK_TEMPLATE_QUERY,
            variables: { filter: { ids: templatesIds } }
          }),
          'data.riskTemplates.data',
          []
        )
      }

      if (parentTemplates.length) {
        setParentRiskTemplates(parentTemplates)
      }

      const options = parentTemplates.map((item) => ({
        name: item.name,
        fields: item.fields.filter(filterBySource).flatMap((field) => {
          const parentLabel = field.parentFieldId
            ? riskTemplate?.fields?.find((f) => f.id === field.parentFieldId)?.label
            : ''

          return getTemplateFieldsOptions({ field, parentLabel, isParentField: true })
        })
      }))

      setParentFieldsOptions(options)
    } catch (e) {
      handleError(e, 'SendEmail.getParentFieldsOptions')
    }
  }

  React.useEffect(() => {
    if (isRiskTemplate) {
      getFieldsOptions()
      getParentFieldsOptions()
    }
  }, [])

  return (
    <Wrapper>
      <FieldArray<any>
        name={`${field}.recipients`}
        component={renderRecipients}
        riskTemplateFields={fieldsOptions}
        recipientsCount={recipients?.length}
        parentRiskTemplatesFields={parentFieldsOptions}
      />
      <FieldWrapper width={'100%'} style={FIELD_STYLES}>
        <AddMetatagButton
          addMetatag={addMetatag}
          parentRiskTemplates={parentRiskTemplates}
          testId={`${field}.subject-metatag`}
        />
        <ReduxInput validate={required} inputRef={inputRef} label='Email Subject' name={`${field}.subject`} />
      </FieldWrapper>
      <EmailEditor
        field={field}
        template={template}
        showEmailPreview={showEmailPreview}
        parentRiskTemplates={parentRiskTemplates}
      />
    </Wrapper>
  )
}

export default SendEmail
